import React, {useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {CalendarOff, Pencil, Plus, Trash2, X} from "lucide-react";
import toast from "react-hot-toast";
import usePermissions from "../../../hooks/usePermissions";
import {keyedSlot, restSlotsError, saveRestSlots} from "../../../calander/restDays";
import Button from "../../Common/Button";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import {
    apiErrorMessage,
    createRestDays,
    deleteRestDay,
    fetchRestDays,
    formatHour,
    timeSlots,
    updateRestDay,
    WEEKDAYS,
} from "./calendarSettingsApi";
import {Modal, ModalFooter, FormField, SwitchRow, SettingsTable, SettingsToolbar, AddAction, ListLoader, inputClass} from "../SettingsUI";
import AppleSelect from "../../Common/AppleSelect";
import DateInput from "../../Common/DateInput";

const REST_DAYS_KEY = ["calendar-rest-days"];

// Same range as the tablets' "Repos" form (9h-23h, quarter hours).
const REST_SLOTS = timeSlots(9, 23);
const REST_OPTIONS = REST_SLOTS.map((v) => ({value: v, label: formatHour(v)}));
const FULL_DAY = {start_time: 9, end_time: 23};

const ymd = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const today = () => ymd(new Date());

// Tablets' defaults: Monday, 9h-17h, recurring, for one month.
const emptyForm = () => {
    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    return {
        employee_ids: [],
        slots: [{days_of_week: [1], start_time: 9, end_time: 17}],
        start_date: today(),
        end_date: ymd(end),
        recurring: true,
    };
};

const formatDate = (value) => (value ? value.split("-").reverse().join("/") : "");

const daysLabel = (days) => {
    if (days.length === 7) return "Tous les jours";
    return WEEKDAYS.filter((d) => days.includes(d.id)).map((d) => d.short).join(", ");
};

const fieldClass = inputClass(false);

// Remembered per browser: the choice is only a way of reading the list
const GROUP_BY_EMPLOYEE_KEY = "restDaysGroupByEmployee";
const readGroupByEmployee = () => {
    try {
        return localStorage.getItem(GROUP_BY_EMPLOYEE_KEY) === "true";
    } catch {
        return false;
    }
};
const writeGroupByEmployee = (value) => {
    try {
        localStorage.setItem(GROUP_BY_EMPLOYEE_KEY, String(value));
    } catch {
        // Private window / blocked storage: the switch still works for this visit
    }
};

const employeeLabel = (rest) => rest.employee_name || "Employé supprimé";

// One section per employee (A → Z), each keeping the order of the list
const groupRestsByEmployee = (rests) => {
    const byEmployee = new Map();
    rests.forEach((rest) => {
        const key = rest.employee_id || "none";
        if (!byEmployee.has(key)) byEmployee.set(key, {key, title: employeeLabel(rest), rows: []});
        byEmployee.get(key).rows.push(rest);
    });
    return [...byEmployee.values()]
        .map((group) => ({...group, count: `${group.rows.length} repos`}))
        .sort((a, b) => a.title.localeCompare(b.title, "fr"));
};

// One "days + hours" block of the form. `title` / `onRemove` only when there are several.
const RestSlotFields = ({slot, title, onChange, onRemove}) => {
    const isFullDay = slot.start_time === FULL_DAY.start_time && slot.end_time === FULL_DAY.end_time;
    const toggleDay = (id) => onChange({
        days_of_week: slot.days_of_week.includes(id)
            ? slot.days_of_week.filter((d) => d !== id)
            : [...slot.days_of_week, id],
    });

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
            {onRemove && (
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</span>
                    <Button variant="ghost" size="sm" icon={X} title={`Retirer le ${title.toLowerCase()}`} onClick={onRemove}/>
                </div>
            )}
            <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day) => {
                    const selected = slot.days_of_week.includes(day.id);
                    return (
                        <button
                            key={day.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggleDay(day.id)}
                            className={`w-12 py-1.5 rounded-lg text-sm border transition-colors ${
                                selected
                                    ? "bg-primary/10 text-primary border-primary font-medium"
                                    : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            }`}
                        >
                            {day.short}
                        </button>
                    );
                })}
            </div>
            <SwitchRow
                id={`restFullDay-${slot.key}`}
                label="Journée entière"
                checked={isFullDay}
                onChange={(checked) => onChange(checked ? FULL_DAY : {start_time: 9, end_time: 17})}
            />
            <div className="grid grid-cols-2 gap-3">
                <FormField id={`restStart-${slot.key}`} label="De">
                    <AppleSelect id={`restStart-${slot.key}`} value={slot.start_time} options={REST_OPTIONS}
                                 onChange={(e) => onChange({start_time: Number(e.target.value)})}/>
                </FormField>
                <FormField id={`restEnd-${slot.key}`} label="À">
                    <AppleSelect id={`restEnd-${slot.key}`} value={slot.end_time} options={REST_OPTIONS}
                                 onChange={(e) => onChange({end_time: Number(e.target.value)})}/>
                </FormField>
            </div>
        </div>
    );
};

const RestDayForm = ({initial, employees, editing, onCancel, onSubmit, saving}) => {
    const [form, setForm] = useState(() => ({...initial, slots: initial.slots.map(keyedSlot)}));
    const set = (changes) => setForm((prev) => ({...prev, ...changes}));

    const toggleEmployee = (id) => set({
        employee_ids: form.employee_ids.includes(id)
            ? form.employee_ids.filter((v) => v !== id)
            : [...form.employee_ids, id],
    });
    const updateSlot = (key, changes) =>
        set({slots: form.slots.map((s) => (s.key === key ? {...s, ...changes} : s))});
    const removeSlot = (key) => set({slots: form.slots.filter((s) => s.key !== key)});
    // The next slot starts with no day and the previous slot's hours
    const addSlot = () => {
        const {start_time, end_time} = form.slots[form.slots.length - 1];
        set({slots: [...form.slots, keyedSlot({days_of_week: [], start_time, end_time})]});
    };

    const error =
        (form.employee_ids.length === 0 && "Choisissez au moins un employé") ||
        restSlotsError(form.slots) ||
        (!form.start_date || !form.end_date ? "Dates requises" : null) ||
        (form.end_date < form.start_date && "La date de fin doit être après la date de début") ||
        null;

    return (
        <Modal
            title={editing ? "Modifier le repos" : "Nouveau repos"}
            onClose={onCancel}
            footer={<ModalFooter onCancel={onCancel} onConfirm={() => onSubmit(form)} saving={saving} disabled={!!error}
                                 label="Enregistrer"/>}
        >
            <div className="space-y-4">
                <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                        {editing ? "Employé" : "Employés"}<span className="text-red-500"> *</span> :
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {employees.map((emp) => {
                            const selected = form.employee_ids.includes(emp.id);
                            return (
                                <button
                                    key={emp.id}
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => (editing ? set({employee_ids: [emp.id]}) : toggleEmployee(emp.id))}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                        selected
                                            ? "bg-primary/10 text-primary border-primary font-medium"
                                            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    }`}
                                >
                                    {emp.name}
                                </button>
                            );
                        })}
                        {employees.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Aucun employé actif.</p>}
                    </div>
                </div>

                <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                        Jours et horaires<span className="text-red-500"> *</span> :
                    </span>
                    <div className="space-y-3">
                        {form.slots.map((slot, index) => (
                            <RestSlotFields
                                key={slot.key}
                                slot={slot}
                                title={`Créneau ${index + 1}`}
                                onChange={(changes) => updateSlot(slot.key, changes)}
                                onRemove={form.slots.length > 1 ? () => removeSlot(slot.key) : undefined}
                            />
                        ))}
                    </div>
                    <Button variant="plain" size="sm" icon={Plus} onClick={addSlot} className="mt-2">
                        Ajouter un créneau
                    </Button>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <FormField id="restFrom" label="Du" required>
                            <DateInput id="restFrom" className={fieldClass} value={form.start_date}
                                   onChange={(e) => set({start_date: e.target.value})}/>
                        </FormField>
                        <FormField id="restTo" label="Au" required>
                            <DateInput id="restTo" className={fieldClass} value={form.end_date} min={form.start_date}
                                   onChange={(e) => set({end_date: e.target.value})}/>
                        </FormField>
                    </div>
                    <SwitchRow
                        id="restRecurring"
                        label="Récurrent (chaque semaine)"
                        checked={form.recurring}
                        onChange={(checked) => set({recurring: checked})}
                    />
                </div>

                {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
        </Modal>
    );
};

// Réglages > Calendrier > Repos des employés: the "Repos" the tablets show
// (greyed hours, no booking for that employee, also used by online booking).
const RestDaysSettings = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("settings");
    const queryClient = useQueryClient();
    const [showPast, setShowPast] = useState(false);
    const [employeeFilter, setEmployeeFilter] = useState("");
    const [groupByEmployee, setGroupByEmployee] = useState(readGroupByEmployee);
    const [formState, setFormState] = useState(null); // {initial, id?}
    const [toDelete, setToDelete] = useState(null);

    const from = showPast ? null : today();
    const {data, isLoading, isError} = useQuery({
        queryKey: [...REST_DAYS_KEY, from],
        queryFn: () => fetchRestDays({from}),
        staleTime: 0,
    });
    const employees = data?.employees || [];

    const visible = useMemo(
        () => (data?.restDays || [])
            .filter((r) => !employeeFilter || r.employee_id === employeeFilter)
            .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")),
        [data, employeeFilter]
    );

    const groups = useMemo(() => (groupByEmployee ? groupRestsByEmployee(visible) : null), [groupByEmployee, visible]);

    const refresh = () => queryClient.invalidateQueries({queryKey: REST_DAYS_KEY});

    const saveMutation = useMutation({
        mutationFn: ({id, form}) => saveRestSlots(form, {id, create: createRestDays, update: updateRestDay}),
        onSuccess: ({saved, total, error}, {id, form}) => {
            if (saved > 0) refresh();

            if (!error) {
                setFormState(null);
                if (total > 1) toast.success(`${total} créneaux de repos enregistrés — envoyés aux caisses`);
                else toast.success(id ? "Repos modifié — envoyé aux caisses" : "Repos ajouté — envoyé aux caisses");
                return;
            }

            const message = apiErrorMessage(error, "Impossible d'enregistrer le repos");
            toast.error(saved > 0 ? `${saved} créneau(x) sur ${total} enregistré(s). ${message}` : message);
            // Keep only what's left to save: the saved slots must not be sent twice on retry
            if (saved > 0) setFormState({formKey: Date.now(), initial: {...form, slots: form.slots.slice(saved)}});
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteRestDay(id),
        onSuccess: () => {
            refresh();
            toast.success("Repos supprimé");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible de supprimer le repos")),
    });

    const openEdit = (restDay) =>
        setFormState({
            id: restDay.id,
            initial: {
                employee_ids: restDay.employee_id ? [restDay.employee_id] : [],
                slots: [{days_of_week: restDay.days_of_week, start_time: restDay.start_time, end_time: restDay.end_time}],
                start_date: restDay.start_date,
                end_date: restDay.end_date,
                recurring: restDay.recurring,
            },
        });

    if (isError && !data) {
        return <p className="text-sm text-red-500 p-6">Impossible de charger les repos.</p>;
    }

    const openCreate = () => setFormState({initial: {
        ...emptyForm(),
        employee_ids: employeeFilter ? [employeeFilter] : [],
    }});

    const columns = [
        // Already the title of each section when grouped
        ...(groupByEmployee ? [] : [{key: "employee", label: "Employé", render: employeeLabel}]),
        {key: "days", label: "Jours", render: (r) => daysLabel(r.days_of_week)},
        {key: "hours", label: "Horaires", className: "tabular-nums whitespace-nowrap",
            render: (r) => `${formatHour(r.start_time)}–${formatHour(r.end_time)}`},
        {key: "period", label: "Période", className: "whitespace-nowrap", render: (r) => (
            <>
                {r.start_date === r.end_date
                    ? `le ${formatDate(r.start_date)}`
                    : `du ${formatDate(r.start_date)} au ${formatDate(r.end_date)}`}
                {r.recurring && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        récurrent
                    </span>
                )}
            </>
        )},
    ];

    const rowActions = (restDay) => [
        {label: "Modifier", icon: Pencil, onClick: () => openEdit(restDay), tone: "blue"},
        {label: "Supprimer", icon: Trash2, onClick: () => setToDelete(restDay), tone: "red", danger: true},
    ];

    return (
        <div>
            {canManage && <AddAction onClick={openCreate}/>}

            {/* ── Toolbar ── */}
            <SettingsToolbar>
                <AppleSelect size="sm" value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}
                             aria-label="Filtrer par employé" className="sm:min-w-[14rem]"
                             options={[{value: '', label: 'Tous les employés'}, ...employees.map((emp) => ({value: emp.id, label: emp.name}))]}/>
                <SwitchRow id="restShowPast" label="Afficher les repos passés" checked={showPast} onChange={setShowPast}/>
                <SwitchRow id="restGroupByEmployee" label="Regrouper par employé" checked={groupByEmployee}
                           onChange={(checked) => {
                               setGroupByEmployee(checked);
                               writeGroupByEmployee(checked);
                           }}/>
            </SettingsToolbar>

            {isLoading ? (
                <ListLoader label="Chargement des repos…"/>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={visible}
                    groups={groups}
                    rowActions={canManage ? rowActions : undefined}
                    onRowClick={canManage ? openEdit : undefined}
                    empty={{
                        icon: CalendarOff,
                        title: `Aucun repos ${showPast ? "" : "à venir"}`.trim(),
                        text: "Les repos bloquent la réservation pour l'employé concerné.",
                    }}
                />
            )}

            <p className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400">
                Les repos sont envoyés automatiquement aux caisses et bloquent la réservation en ligne
                pour l'employé concerné. Ceux saisis sur les caisses apparaissent aussi ici.
            </p>

            {formState && (
                <RestDayForm
                    key={formState.formKey}
                    initial={formState.initial}
                    employees={employees}
                    editing={!!formState.id}
                    saving={saveMutation.isPending}
                    onCancel={() => setFormState(null)}
                    onSubmit={(form) => saveMutation.mutate({id: formState.id, form})}
                />
            )}

            {toDelete && (
                <ConfirmationDialog
                    title="Supprimer ce repos ?"
                    message={`${toDelete.employee_name || "Employé"} — ${daysLabel(toDelete.days_of_week)}, du ${formatDate(toDelete.start_date)} au ${formatDate(toDelete.end_date)}. Il sera aussi retiré des caisses.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    danger
                    onCancel={() => setToDelete(null)}
                    onConfirm={() => {
                        deleteMutation.mutate(toDelete.id);
                        setToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default RestDaysSettings;
